import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { updateLeadBreadcrumbItems } from "data/commonData";
import { Col, Form, Row, Badge, Spinner } from "react-bootstrap";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "helpers/api";
import toast from "react-hot-toast";
import { SubmitHandler, useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faTrash, 
  faBrain, 
  faUser,  
  faMapMarkerAlt, 
  faFileAlt, 
  faTag, 
  faRobot,
  faBolt
} from "@fortawesome/free-solid-svg-icons";
import AddressSearchComponent from "components/common/AddressSearchComponent";
import axios from "axios";

type Inputs = {
  first_name: string;
  last_name: string;
  email: string;
  additional_email: string;
  phone: string;
  lead_source: string;
  city: string;
  state: string;
  zip_code: string;
  street_name: string;
  description: string;
};

const EditLead = () => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<Inputs>();

  const [storeTags, setStoreTags] = useState<any>([]);
  const [tags, setTags] = useState<any>("");
  const [loading, setLoading] = useState(false);
  let { id } = useParams();
  const navigate = useNavigate();

  const [leadType, setLeadType] = useState<string | undefined>(undefined);
  const [leadStatus, setLeadStatus] = useState<any>(null);
  const [leadTypes, setLeadTypes] = useState([]);
  const [selectedLeadStatus, setSelectedLeadStatus] = useState([]);

  const fetchLeadTypes = async () => {
    try {
      const response = await axiosInstance.get("/leads/types");
      setLeadTypes(response.data.data.leadTypes);
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    }
  };

  const matchStatus = async (id: any) => {
    setLeadType(id);

    try {
      const response = await axiosInstance.get(`/leads/status/${id}`);
      setSelectedLeadStatus(response.data.data.leadTypes);
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };
  const handleSelectStatus = (e: any) => {
    setLeadStatus(e);
  };

  useEffect(() => {
    fetchLeadTypes();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      if (!id) return;
      // setLoading(true);
      try {
        const response = await axiosInstance.get(`/leads/${id}`, {
          signal: controller.signal,
        });

        const leadDetails = response?.data?.data?.lead || response?.data?.lead || response?.data;
        if (!leadDetails) throw new Error('Lead data not found in response');

        setValue("first_name", leadDetails.lead_first_name || "");
              setValue("last_name", leadDetails.lead_last_name || "");
        setValue("email", leadDetails.lead_email || "");
        setValue("additional_email", leadDetails.lead_alternative_email || leadDetails.email || "");
        setValue("phone", leadDetails.lead_phone || "");
        setValue("lead_source", leadDetails.lead_source || "");
        setValue("city", leadDetails.lead_city || "");
        setValue("state", leadDetails.lead_state || "");
        setValue("zip_code", leadDetails.lead_zip_code || "");
        setValue("street_name", leadDetails.lead_street_name || "");
        setValue("description", leadDetails.lead_description || "");
        setStoreTags(leadDetails.lead_tags ? leadDetails.lead_tags : []);
        setLeadType(leadDetails.lead_types_id);

        if (leadDetails.lead_types_id) {
          try {
            const getTypes = await axiosInstance.get(`/leads/status/${leadDetails.lead_types_id}`, {
              signal: controller.signal,
            });
            const types = getTypes?.data?.data?.leadTypes || getTypes?.data?.leadTypes || [];
            setSelectedLeadStatus(types);

            const filterTypes = types.find(
              (item: any) => item.id === leadDetails.lead_type_status_id
            );
            if (filterTypes) setLeadStatus(filterTypes.id);
          } catch (statusError) {
            console.warn('⚠️ [EDIT LEAD] Failed to fetch lead status:', statusError);
          }
        }
      } catch (error: any) {
        if (axios.isCancel(error)) {
          console.log('🛑 Fetch aborted');
        } else {
          console.error('❌ [EDIT LEAD] Error fetching lead data:', error);
          toast.error(error?.response?.data?.message || error?.message || 'Failed to fetch lead data');
        }
      }
    };

    fetchData();
    return () => {
      controller.abort(); // Cleanup
    };
  }, [id, setValue]);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      const updatePayload = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        alternative_email: data.additional_email,
        phone: data.phone,
        city: data.city,
        state: data.state,
        lead_source: data.lead_source,
        zip_code: data.zip_code,
        street_name: data.street_name,
        description: data.description,
        lead_types_id: +(leadType ?? 0),
        lead_type_status_id: +leadStatus,
        lead_tags: storeTags,
      };

      setLoading(true)

      const response = await axiosInstance.patch(`/leads/${id}`, updatePayload);
            
      const successMessage = response?.data?.message || 'Lead updated successfully!';
      toast.success(successMessage);
      reset();
      navigate(`/leads/${id}`);
    } catch (error: any) {
      console.error('❌ [EDIT LEAD] Update error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update lead';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTags = () => {
    if (tags === "") {
      toast("Tags Empty");
      return;
    }

    setStoreTags([...storeTags, { tags }]);
    toast.success("Tag Added");
    setTags("");
  };

  const handleDeleteTag = (tag: any) => {
    const filterTags = storeTags.filter((item: any) => item.tags !== tag);
    setStoreTags(filterTags);
  };

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        .edit-lead-container .ai-form-section {
          transition: all 0.3s ease;
        }
        .edit-lead-container .ai-form-section:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
        }
        .ai-input-group .form-control:focus {
          transform: translateY(-1px);
        }
      `}</style>
      <div 
        className="mb-9"
        style={{
          minHeight: '100vh',
          position: 'relative'
        }}
      >
      <PageBreadcrumb items={updateLeadBreadcrumbItems} />
      
      {/* Media query styles for responsive design */}
      <style>
        {`
          @media (max-width: 500px) {
            .edit-lead-container .ai-title {
              font-size: 1.25rem !important;
              text-align: center !important;
            }
            .edit-lead-container .intelligence-header {
              padding-bottom: 45px !important;
            }
            .edit-lead-container .ai-status-indicator {
              top: auto !important;
              bottom: 10px !important;
              right: auto !important;
              left: 50% !important;
              transform: translateX(-50%) !important;
              margin-right: 0 !important;
            }
            .edit-lead-container .d-flex.align-items-center {
              flex-direction: column !important;
              text-align: center !important;
            }
            .edit-lead-container .d-flex.align-items-center > div:first-child {
              margin-right: 0 !important;
              margin-bottom: 12px !important;
            }
            .edit-lead-container .intelligence-header p {
              text-align: center !important;
            }
          }
        `}
      </style>
      
      {/* AI Intelligence Header */}
      <div className="edit-lead-container">
      <div 
        className="intelligence-header mb-4"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
          borderRadius: '16px',
          padding: '20px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          backdropFilter: 'blur(15px)',
          margin: '24px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Row>
          <Col xl={9}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <div style={{ position: 'relative', marginRight: '16px' }}>
                  <FontAwesomeIcon 
                    icon={faBrain} 
                    style={{ 
                      color: '#00d4ff', 
                      fontSize: '32px',
                      filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.5))',
                      animation: 'pulse 2s infinite'
                    }}
                  />
                </div>
                <div>
                  <h2 
                    className="ai-title mb-1"
                    style={{
                      background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      fontSize: '28px',
                      fontWeight: 700
                    }}
                  >
                    AI Lead Intelligence Update
                  </h2>
                  <p 
                    className="mb-0"
                    style={{ 
                      color: '#64748b', 
                      fontSize: '14px',
                      fontWeight: 500
                    }}
                  >
                    Enhanced lead profiling with neural network validation
                  </p>
                </div>
              </div>
              <Badge 
                className="ai-status-indicator"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#ffffff',
                  fontSize: '10px',
                  fontWeight: 600,
                  padding: '6px 12px',
                  marginRight: '16px',
                  borderRadius: '20px',
                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                  animation: 'pulse 2s infinite',
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <FontAwesomeIcon icon={faRobot} className="me-1" />
                AI ACTIVE
              </Badge>
            </div>
                        </Col>
            </Row>
          </div>
      <Row>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div 
            className="mb-6 ai-form-section"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
              backdropFilter: 'blur(15px)',
              borderRadius: '16px',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              margin: '24px',
              padding: '24px'
            }}
          >
            <div 
              className="ai-section-header mb-4"
              style={{
                background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px'
              }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <div 
                    className="ai-icon-container me-3"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)'
                    }}
                  >
                    <FontAwesomeIcon icon={faUser} style={{ color: '#ffffff', fontSize: '18px' }} />
                  </div>
                  <div>
                    <h4 
                      className="mb-1"
                      style={{
                        color: '#1f2937',
                        fontWeight: 600,
                        fontSize: '18px'
                      }}
                    >
                      Lead Information
                    </h4>
                    <small 
                      style={{ 
                        color: '#64748b', 
                        fontSize: '12px' 
                      }}
                    >
                      Neural network insights & validation
                    </small>
                  </div>
                </div>
                <Badge 
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: '#ffffff',
                    fontSize: '9px',
                    fontWeight: 600,
                    padding: '4px 8px',
                    borderRadius: '12px'
                  }}
                >
                  <FontAwesomeIcon icon={faBolt} className="me-1" />
                  AI PROCESSING
                </Badge>
              </div>
            </div>
                          <Row className="g-3">
                <Col sm={6} md={4}>
                  <Form.Group>
                    <Form.Label>First name<span className="text-danger"> *</span></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="First name"
                      {...register("first_name", {
                        required: true,
                        maxLength: 100,
                      })}
                    />
                    {errors.first_name?.type === "required" && (
                      <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                        First name is required
                      </p>
                    )}
                  </Form.Group>
                </Col>
              <Col sm={6} md={4}>
                <Form.Group>
                  <Form.Label>Last name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Last name"
                    {...register("last_name")}
                  />
                </Form.Group>
              </Col>

              <Col sm={6} md={4}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Email"
                    {...register("email", { maxLength: 80 })}
                  />
                </Form.Group>
              </Col>
              <Col sm={6} md={4}>
                <Form.Group>
                  <Form.Label>Alternative email</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Alternative email"
                    {...register("additional_email", { maxLength: 80 })}
                  />
                </Form.Group>
              </Col>
              <Col sm={6} md={4}>
                <Form.Group>
                  <Form.Label>
                    Phone <span className="text-danger"> *</span>
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    inputMode="tel"
                    placeholder="Phone"
                    className="input-spin-none"
                    {...register("phone", { required: true, maxLength: 80 })}
                  />
                </Form.Group>

                {errors.phone?.type === "required" && (
                  <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                    Phone Number is required
                  </p>
                )}
              </Col>

              <Col sm={6} md={4}>
                <Form.Group>
                  <Form.Label>Source</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Source"
                    className="input-spin-none"
                    {...register("lead_source", { maxLength: 80 })}
                  />
                </Form.Group>
              </Col>

              <Col sm={6} md={4}>
                <Form.Group>
                  <Form.Label>Lead Type</Form.Label>
                  <Form.Select
                    value={leadType}
                    onChange={(e) => matchStatus(e.target.value)}
                  >
                    <option value="" disabled selected>
                      Select
                    </option>
                    {leadTypes.map((item: any, i: any) => (
                      <option key={i} value={item.id}>
                        {item.lead_type}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col sm={6} md={4}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={leadStatus}
                    className="form-select"
                    onChange={(e) => handleSelectStatus(e.target.value)}
                  >
                    <option value="" disabled selected>
                      Select
                    </option>
                    {selectedLeadStatus.map((item: any, i: any) => (
                      <option key={i} value={item.id}>
                        {item.status}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div 
            className="mb-6 ai-form-section"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
              backdropFilter: 'blur(15px)',
              borderRadius: '16px',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              margin: '24px',
              padding: '24px'
            }}
          >
            <div 
              className="ai-section-header mb-4"
              style={{
                background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px'
              }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <div 
                    className="ai-icon-container me-3"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 20px rgba(139, 92, 246, 0.3)'
                    }}
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: '#ffffff', fontSize: '18px' }} />
                  </div>
                  <div>
                    <h4 
                      className="mb-1"
                      style={{
                        color: '#1f2937',
                        fontWeight: 600,
                        fontSize: '18px'
                      }}
                    >
                      Address Intelligence
                    </h4>
                    <small 
                      style={{ 
                        color: '#64748b', 
                        fontSize: '12px' 
                      }}
                    >
                      Location-based market insights
                    </small>
                  </div>
                </div>
                <Badge 
                  style={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: '#ffffff',
                    fontSize: '9px',
                    fontWeight: 600,
                    padding: '4px 8px',
                    borderRadius: '12px'
                  }}
                >
                  <FontAwesomeIcon icon={faBolt} className="me-1" />
                  GEO AI
                </Badge>
              </div>
            </div>
            
            {/* AI-Powered Address Search */}
            <Row className="g-3">
              <Col lg={12}>
                <AddressSearchComponent
                  label="Complete Address"
                  placeholder="🏠 Start typing to search for address..."
                  value={watch('street_name') || ''}
                  onChange={(value) => {
                    // Just set the street name for manual typing
                    setValue('street_name', value);
                  }}
                  onAddressSelect={(suggestion) => {
                    console.log('🏠 Address selected:', suggestion);
                    
                    // Enhanced address parsing for Mapbox format
                    // Example: "1027 South Lola Lane, Tempe, Arizona 85281, United States"
                    const parseMapboxAddress = (address: string) => {
                      const parts = address.split(',').map(part => part.trim());
                      console.log('📍 Address parts:', parts);
                      
                      if (parts.length >= 3) {
                        const streetAddress = parts[0]; // "1027 South Lola Lane"
                        const city = parts[1]; // "Tempe"
                        const stateZipPart = parts[2]; // "Arizona 85281" or "AZ 85281"
                        
                        // Extract state and zip from the state/zip part
                        const stateZipMatch = stateZipPart.match(/^(.+?)\s+(\d{5}(?:-\d{4})?)$/);
                        
                        if (stateZipMatch) {
                          const stateName = stateZipMatch[1].trim(); // "Arizona" or "AZ"
                          const zipCode = stateZipMatch[2]; // "85281"
                          
                          // Convert full state names to abbreviations if needed
                          const stateAbbreviations: {[key: string]: string} = {
                            'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
                            'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
                            'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI', 'Idaho': 'ID',
                            'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
                            'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
                            'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
                            'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
                            'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
                            'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
                            'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
                            'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT',
                            'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV',
                            'Wisconsin': 'WI', 'Wyoming': 'WY'
                          };
                          
                          const finalState = stateAbbreviations[stateName] || stateName;
                          
                          console.log('✅ Parsed address:', {
                            street: streetAddress,
                            city: city,
                            state: finalState,
                            zip: zipCode
                          });
                          
                          return {
                            street: streetAddress,
                            city: city,
                            state: finalState,
                            zip: zipCode
                          };
                        }
                      }
                      
                      // Fallback for incomplete addresses
                      return {
                        street: parts[0] || address,
                        city: parts[1] || '',
                        state: '',
                        zip: ''
                      };
                    };
                    
                    const parsed = parseMapboxAddress(suggestion.address);
                    
                    // Set all form values
                    setValue('street_name', parsed.street, { shouldValidate: true });
                    setValue('city', parsed.city, { shouldValidate: true });
                    setValue('state', parsed.state, { shouldValidate: true });
                    setValue('zip_code', parsed.zip, { shouldValidate: true });
                    
                    // Show success message
                    toast.success(`Address auto-filled: ${parsed.city}, ${parsed.state} ${parsed.zip}`);
                  }}
                />
              </Col>
            </Row>
            
            {/* Individual Address Components (Auto-filled from search) */}
            <Row className="g-3 mt-3">
              <Col sm={6} md={6}>
                <Form.Group className="ai-input-group">
                  <Form.Label 
                    className="ai-label"
                    style={{
                      color: '#374151',
                      fontWeight: 600,
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" style={{ color: '#8b5cf6' }} />
                    Street Address
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Street Address"
                    {...register("street_name")}
                    style={{
                      background: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#8b5cf6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(139, 92, 246, 0.3)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    }}
                  />
                </Form.Group>
              </Col>

              <Col sm={6} md={6}>
                <Form.Group className="ai-input-group">
                  <Form.Label 
                    className="ai-label"
                    style={{
                      color: '#374151',
                      fontWeight: 600,
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" style={{ color: '#8b5cf6' }} />
                    City
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="City"
                    {...register("city")}
                    style={{
                      background: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#8b5cf6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(139, 92, 246, 0.3)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    }}
                  />
                </Form.Group>
              </Col>
              
              <Col sm={6} md={3}>
                <Form.Group className="ai-input-group">
                  <Form.Label 
                    className="ai-label"
                    style={{
                      color: '#374151',
                      fontWeight: 600,
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" style={{ color: '#8b5cf6' }} />
                    State
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="State"
                    {...register("state")}
                    style={{
                      background: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#8b5cf6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(139, 92, 246, 0.3)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    }}
                  />
                </Form.Group>
              </Col>
              
              <Col sm={6} md={3}>
                <Form.Group className="ai-input-group">
                  <Form.Label 
                    className="ai-label"
                    style={{
                      color: '#374151',
                      fontWeight: 600,
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" style={{ color: '#8b5cf6' }} />
                    Zip Code
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Zip Code"
                    {...register("zip_code")}
                    style={{
                      background: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#8b5cf6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(139, 92, 246, 0.3)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div 
            className="mb-6 ai-form-section"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
              backdropFilter: 'blur(15px)',
              borderRadius: '16px',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              margin: '24px',
              padding: '24px'
            }}
          >
            <div 
              className="ai-section-header mb-4"
              style={{
                background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px'
              }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <div 
                    className="ai-icon-container me-3"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 20px rgba(16, 185, 129, 0.3)'
                    }}
                  >
                    <FontAwesomeIcon icon={faFileAlt} style={{ color: '#ffffff', fontSize: '18px' }} />
                  </div>
                  <div>
                    <h4 
                      className="mb-1"
                      style={{
                        color: '#1f2937',
                        fontWeight: 600,
                        fontSize: '18px'
                      }}
                    >
                      AI Profile Summary
                    </h4>
                    <small 
                      style={{ 
                        color: '#64748b', 
                        fontSize: '12px' 
                      }}
                    >
                      Smart content analysis & insights
                    </small>
                  </div>
                </div>
                <Badge 
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#ffffff',
                    fontSize: '9px',
                    fontWeight: 600,
                    padding: '4px 8px',
                    borderRadius: '12px'
                  }}
                >
                  <FontAwesomeIcon icon={faBolt} className="me-1" />
                  NLP AI
                </Badge>
              </div>
            </div>

            <Form.Group className="ai-input-group">
              <Form.Label 
                className="ai-label"
                style={{
                  color: '#374151',
                  fontWeight: 600,
                  fontSize: '14px',
                  marginBottom: '8px'
                }}
              >
                <FontAwesomeIcon icon={faFileAlt} className="me-2" style={{ color: '#10b981' }} />
                Lead Profile Summary
              </Form.Label>
              <Form.Control
                as="textarea"
                style={{ 
                  height: "128px",
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  fontSize: '14px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease'
                }}
                type="text"
                placeholder="Add Summary"
                {...register("description")}
                onFocus={(e) => {
                  e.target.style.borderColor = '#10b981';
                  e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(0, 212, 255, 0.3)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              />
            </Form.Group>
          </div>

          <div 
            className="mb-6 ai-form-section"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
              backdropFilter: 'blur(15px)',
              borderRadius: '16px',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              margin: '24px',
              padding: '24px'
            }}
          >
            <div 
              className="ai-section-header mb-4"
              style={{
                background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px'
              }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <div 
                    className="ai-icon-container me-3"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 20px rgba(245, 158, 11, 0.3)'
                    }}
                  >
                    <FontAwesomeIcon icon={faTag} style={{ color: '#ffffff', fontSize: '18px' }} />
                  </div>
                  <div>
                    <h4 
                      className="mb-1"
                      style={{
                        color: '#1f2937',
                        fontWeight: 600,
                        fontSize: '18px'
                      }}
                    >
                      Smart Tags & Labels
                    </h4>
                    <small 
                      style={{ 
                        color: '#64748b', 
                        fontSize: '12px' 
                      }}
                    >
                      AI-powered categorization system
                    </small>
                  </div>
                </div>
                <Badge 
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: '#ffffff',
                    fontSize: '9px',
                    fontWeight: 600,
                    padding: '4px 8px',
                    borderRadius: '12px'
                  }}
                >
                  <FontAwesomeIcon icon={faBolt} className="me-1" />
                  TAG AI
                </Badge>
              </div>
            </div>

            <div 
              className="ai-tags-input-container mb-4"
              style={{
                background: 'rgba(255, 255, 255, 0.5)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                border: '1px solid rgba(245, 158, 11, 0.2)',
                padding: '20px',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Row className="g-3 align-items-end">
                <Col md={8}>
                  <Form.Group className="ai-input-group">
                    <Form.Label 
                      className="ai-label"
                      style={{
                        color: '#374151',
                        fontWeight: 600,
                        fontSize: '14px',
                        marginBottom: '8px'
                      }}
                    >
                      <FontAwesomeIcon icon={faTag} className="me-2" style={{ color: '#f59e0b' }} />
                      New Tag
                    </Form.Label>
                    <Form.Control
                      value={tags}
                      type="text"
                      placeholder="Enter intelligent tag..."
                      onChange={(e) => setTags(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleTags();
                        }
                      }}
                      style={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        fontSize: '14px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#f59e0b';
                        e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'rgba(245, 158, 11, 0.3)';
                        e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Button
                    onClick={handleTags}
                    className="w-100 ai-tag-button"
                    style={{
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#ffffff',
                      boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                      transition: 'all 0.3s ease',
                      height: '48px'
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLElement).style.transform = 'translateY(-2px)';
                      (e.target as HTMLElement).style.boxShadow = '0 6px 20px rgba(245, 158, 11, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLElement).style.transform = 'translateY(0)';
                      (e.target as HTMLElement).style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
                    }}
                  >
                    <FontAwesomeIcon icon={faTag} className="me-2" />
                    Add Tag
                  </Button>
                </Col>
              </Row>
            </div>

            {/* AI-Enhanced Tags Display */}
            {storeTags && storeTags.length > 0 && (
              <div 
                className="ai-tags-display"
                style={{
                  background: 'rgba(255, 255, 255, 0.3)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '12px',
                  border: '1px solid rgba(245, 158, 11, 0.15)',
                  padding: '16px'
                }}
              >
                <div className="d-flex flex-wrap gap-3">
                  {storeTags.map((item: any, i: any) => (
                    <div 
                      key={i}
                      className="ai-tag-item d-flex align-items-center"
                      style={{
                        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.05) 100%)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(245, 158, 11, 0.2)',
                        borderRadius: '20px',
                        padding: '8px 16px',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#92400e',
                        boxShadow: '0 2px 8px rgba(245, 158, 11, 0.1)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        (e.target as HTMLElement).style.transform = 'translateY(-1px)';
                        (e.target as HTMLElement).style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        (e.target as HTMLElement).style.transform = 'translateY(0)';
                        (e.target as HTMLElement).style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.1)';
                      }}
                    >
                      <FontAwesomeIcon icon={faTag} className="me-2" style={{ fontSize: '11px' }} />
                      <span>#{item.tags}</span>
                      <Button
                        variant="link"
                        size="sm"
                        className="ms-2 p-0 ai-tag-delete"
                        onClick={() => handleDeleteTag(item.tags)}
                        style={{
                          color: '#dc2626',
                          fontSize: '12px',
                          border: 'none',
                          background: 'none',
                          padding: '0',
                          minWidth: 'auto',
                          width: '16px',
                          height: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          (e.target as HTMLElement).style.background = 'rgba(220, 38, 38, 0.1)';
                          (e.target as HTMLElement).style.color = '#dc2626';
                        }}
                        onMouseLeave={(e) => {
                          (e.target as HTMLElement).style.background = 'none';
                          (e.target as HTMLElement).style.color = '#dc2626';
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} style={{ fontSize: '10px' }} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>


          <div className="text-end" style={{ padding: "0 24px 0 24px"}}>
            <Button
              type="submit"
              className="mt-2 ai-submit-button"
              disabled={loading}
              style={{
                background: loading 
                  ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
                  : 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                border: 'none',
                borderRadius: '12px',
                padding: '12px 32px',
                fontSize: '16px',
                fontWeight: 600,
                color: '#ffffff',
                boxShadow: loading 
                  ? '0 4px 16px rgba(107, 114, 128, 0.3)'
                  : '0 4px 16px rgba(0, 212, 255, 0.3)',
                transition: 'all 0.3s ease',
                minWidth: '140px'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  (e.target as HTMLElement).style.transform = 'translateY(-2px)';
                  (e.target as HTMLElement).style.boxShadow = '0 8px 24px rgba(0, 212, 255, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  (e.target as HTMLElement).style.transform = 'translateY(0)';
                  (e.target as HTMLElement).style.boxShadow = '0 4px 16px rgba(0, 212, 255, 0.3)';
                }
              }}
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faBrain} className="me-2" />
                  Update with AI
                </>
              )}
            </Button>
          </div>
        </Form>
      </Row>
    </div>
    </div>
    </>
  );
};

export default EditLead;
